import supabase from '../config/supabase';

/**
 * Tape (mobile count strip) edit propagation: reorder and remove items, with
 * the same propagation pattern used for PAR/MOQ edits — best-effort writes to
 * the parent template (count_template_items + sibling is_template count_session
 * count_items) so future counts created from the template inherit the change.
 *
 * Anchors:
 *   - Per-count sort lives on count_items.count_position (no unique constraint).
 *   - Per-template sort lives on count_template_items.template_sort_position
 *     with UNIQUE(template_id, location, template_sort_position). Reorders
 *     therefore use a two-phase update (negative offset → final value) so the
 *     intermediate state never collides with another row.
 *   - The is_template=true sibling count_session in the same count_group also
 *     stores its own count_items rows; we mirror count_position there too so
 *     the template editor view reads the new order immediately.
 *
 * Failures are logged but never thrown — the in-session reorder/delete has
 * already been persisted by the caller before propagation runs.
 */

const POS_OFFSET = 1000000; // out-of-band base for the 2-phase update.

/**
 * Persist a new tape order to the current count_session and propagate it to
 * the parent template.
 *
 * @param {Object}   args
 * @param {string}   args.sessionId           current count_session id
 * @param {string}   args.templateId          parent template id (may be null)
 * @param {string}   args.countGroupId        sibling group id (may be null)
 * @param {Array}    args.orderedRows         the post-reorder rows in the
 *                   intended order. Each row must include at least
 *                   { id, inventory_item_id, location }.
 *
 * Mutates count_items.count_position by id. For the parent template, only
 * count_template_items rows whose (inventory_item_id, location) appear in
 * orderedRows are renumbered; rows for items not in the current count keep
 * their existing template_sort_position, and we offset our renumbering to
 * sit safely above the maximum untouched position so the unique constraint
 * never collides.
 */
export async function persistTapeReorder({
  sessionId,
  templateId,
  countGroupId,
  orderedRows,
}) {
  if (!sessionId || !Array.isArray(orderedRows) || orderedRows.length === 0) {
    return { ok: false, reason: 'missing-args' };
  }

  // 1) Update count_items.count_position on the active session. Per-row
  //    update by PK — there's no unique constraint on count_position so we
  //    can write directly without a 2-phase shuffle.
  try {
    await Promise.all(
      orderedRows.map((row, idx) =>
        supabase
          .from('count_items')
          .update({ count_position: (idx + 1) * 10 })
          .eq('id', row.id)
      )
    );
  } catch (err) {
    console.warn('[persistTapeReorder] count_items position update failed:', err);
    return { ok: false, reason: 'count-items-update-failed' };
  }

  const result = { ok: true, templatePropagated: false, templateSessionPropagated: false };

  // 2) Mirror the order to count_template_items so the next count created
  //    from the template inherits the new order.
  if (templateId) {
    try {
      // Group target rows by location — template positions are unique per
      // (template_id, location), so each location is renumbered independently.
      const byLocation = new Map();
      orderedRows.forEach((row) => {
        const loc =
          row.location && String(row.location).trim() !== ''
            ? row.location
            : 'No Location';
        if (!byLocation.has(loc)) byLocation.set(loc, []);
        byLocation.get(loc).push(row);
      });

      for (const [loc, rows] of byLocation.entries()) {
        const invIds = [...new Set(rows.map((r) => r.inventory_item_id).filter(Boolean))];
        if (invIds.length === 0) continue;

        // Find the template rows that match these items at this location.
        const { data: ctiRows, error: selErr } = await supabase
          .from('count_template_items')
          .select('id, inventory_item_id, location, template_sort_position')
          .eq('template_id', templateId)
          .in('inventory_item_id', invIds)
          .eq('location', loc);
        if (selErr) {
          console.warn('[persistTapeReorder] count_template_items lookup failed:', selErr);
          continue;
        }
        if (!ctiRows || ctiRows.length === 0) continue;

        // Determine the highest existing position at this location so our
        // renumbering starts above any untouched rows and never collides
        // with the UNIQUE(template_id, location, template_sort_position).
        const { data: allRows } = await supabase
          .from('count_template_items')
          .select('template_sort_position')
          .eq('template_id', templateId)
          .eq('location', loc);
        const maxExisting = (allRows || []).reduce(
          (m, r) => Math.max(m, Number(r.template_sort_position) || 0),
          0
        );

        // Phase 1: shove every affected row out to a unique negative slot
        // keyed by id so the constraint is never violated mid-update.
        await Promise.all(
          ctiRows.map((r) =>
            supabase
              .from('count_template_items')
              .update({ template_sort_position: -(POS_OFFSET + r.id) })
              .eq('id', r.id)
          )
        );

        // Phase 2: assign final positions in the intended order. Items not
        // present in count_template_items at this location are skipped.
        const ctiByItem = new Map();
        ctiRows.forEach((r) => ctiByItem.set(String(r.inventory_item_id), r));

        const renumberedRows = rows.filter((row) =>
          ctiByItem.has(String(row.inventory_item_id))
        );
        const baseStart = maxExisting > 0 ? maxExisting + 10 : 10;
        await Promise.all(
          renumberedRows.map((row, idx) => {
            const cti = ctiByItem.get(String(row.inventory_item_id));
            return supabase
              .from('count_template_items')
              .update({ template_sort_position: baseStart + (idx + 1) * 10 })
              .eq('id', cti.id);
          })
        );

        result.templatePropagated = true;
      }
    } catch (err) {
      console.warn('[persistTapeReorder] template propagation threw:', err);
    }
  }

  // 3) Mirror count_position on the sibling is_template=true count_session
  //    so the template-editor view (which reads count_items for the template
  //    session) reflects the new order immediately.
  if (countGroupId) {
    try {
      const { data: tmplSession } = await supabase
        .from('count_sessions')
        .select('id')
        .eq('count_group_id', countGroupId)
        .eq('is_template', true)
        .maybeSingle();
      if (tmplSession?.id) {
        const tmplSessionId = tmplSession.id;
        const invIds = [...new Set(orderedRows.map((r) => r.inventory_item_id).filter(Boolean))];
        if (invIds.length > 0) {
          const { data: tmplRows } = await supabase
            .from('count_items')
            .select('id, inventory_item_id, location')
            .eq('count_session_id', tmplSessionId)
            .in('inventory_item_id', invIds);
          if (tmplRows && tmplRows.length > 0) {
            const tmplLookup = new Map();
            tmplRows.forEach((r) => {
              const key = `${r.inventory_item_id}::${r.location || ''}`;
              tmplLookup.set(key, r.id);
            });
            await Promise.all(
              orderedRows.map((row, idx) => {
                const key = `${row.inventory_item_id}::${row.location || ''}`;
                const tmplRowId = tmplLookup.get(key);
                if (!tmplRowId) return null;
                return supabase
                  .from('count_items')
                  .update({ count_position: (idx + 1) * 10 })
                  .eq('id', tmplRowId);
              }).filter(Boolean)
            );
            result.templateSessionPropagated = true;
            try {
              if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
                window.dispatchEvent(
                  new CustomEvent('templateSessionItemsUpdated', {
                    detail: { templateSessionId: tmplSessionId, action: 'reordered' },
                  })
                );
              }
            } catch (_) {}
          }
        }
      }
    } catch (err) {
      console.warn('[persistTapeReorder] template-session propagation threw:', err);
    }
  }

  return result;
}

/**
 * Remove an item from the active count, the parent template, and the sibling
 * is_template count_session. Past completed counts are not modified — only
 * the in-progress session and forward-looking template state.
 *
 * @param {Object} args
 * @param {string} args.sessionId           current count_session id
 * @param {string} args.templateId          parent template id (may be null)
 * @param {string} args.countGroupId        sibling group id (may be null)
 * @param {string} args.countItemId         count_items.id to remove from
 *                                          the active session
 * @param {string} args.inventoryItemId     inventory_item_id of the item
 */
export async function persistTapeDelete({
  sessionId,
  templateId,
  countGroupId,
  countItemId,
  inventoryItemId,
}) {
  if (!sessionId || !countItemId || !inventoryItemId) {
    return { ok: false, reason: 'missing-args' };
  }

  const result = {
    ok: true,
    countItemDeleted: false,
    templateDeleted: false,
    templateSessionDeleted: false,
  };

  // 1) Delete the row from the active count_session.
  try {
    const { error: delErr } = await supabase
      .from('count_items')
      .delete()
      .eq('id', countItemId);
    if (delErr) {
      console.warn('[persistTapeDelete] count_items delete failed:', delErr);
      return { ...result, ok: false, reason: 'count-item-delete-failed' };
    }
    result.countItemDeleted = true;
  } catch (err) {
    console.warn('[persistTapeDelete] count_items delete threw:', err);
    return { ...result, ok: false, reason: 'count-item-delete-threw' };
  }

  // 2) Remove the item from the parent template so future counts skip it.
  if (templateId) {
    try {
      const { error: ctiErr } = await supabase
        .from('count_template_items')
        .delete()
        .eq('template_id', templateId)
        .eq('inventory_item_id', inventoryItemId);
      if (ctiErr) {
        console.warn('[persistTapeDelete] count_template_items delete failed:', ctiErr);
      } else {
        result.templateDeleted = true;
      }
    } catch (err) {
      console.warn('[persistTapeDelete] count_template_items delete threw:', err);
    }
  }

  // 3) Remove the item from the sibling is_template count_session so the
  //    template editor view (which reads count_items for that session)
  //    drops the row immediately.
  if (countGroupId) {
    try {
      const { data: tmplSession } = await supabase
        .from('count_sessions')
        .select('id')
        .eq('count_group_id', countGroupId)
        .eq('is_template', true)
        .maybeSingle();
      if (tmplSession?.id) {
        const { error: tmplErr } = await supabase
          .from('count_items')
          .delete()
          .eq('count_session_id', tmplSession.id)
          .eq('inventory_item_id', inventoryItemId);
        if (tmplErr) {
          console.warn('[persistTapeDelete] template-session count_items delete failed:', tmplErr);
        } else {
          result.templateSessionDeleted = true;
          try {
            if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
              window.dispatchEvent(
                new CustomEvent('templateSessionItemsUpdated', {
                  detail: {
                    templateSessionId: tmplSession.id,
                    inventoryItemId,
                    action: 'deleted',
                  },
                })
              );
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      console.warn('[persistTapeDelete] template-session propagation threw:', err);
    }
  }

  return result;
}
